import { Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useFavorites, type FavoriteKind } from "@/hooks/useFavorites";

type Props = {
  /** Legacy prop — same as passing kind="athlete" id={athleteId}. */
  athleteId?: string;
  kind?: FavoriteKind;
  id?: string;
  size?: "sm" | "md";
  className?: string;
};

const LABELS: Record<FavoriteKind, { added: string; signIn: string }> = {
  athlete: { added: "Added to your players", signIn: "Sign in to favorite players" },
  team: { added: "Added to your teams", signIn: "Sign in to favorite teams" },
  product: { added: "Added to your brands", signIn: "Sign in to favorite brands" },
  cause: { added: "Added to your causes", signIn: "Sign in to favorite causes" },
};

const FavoriteButton = ({ athleteId, kind, id, size = "md", className = "" }: Props) => {
  const resolvedKind: FavoriteKind = kind ?? "athlete";
  const targetId = id ?? athleteId ?? "";
  const { user } = useAuth();
  const { favorites, toggle } = useFavorites(resolvedKind);
  const navigate = useNavigate();
  const isFav = favorites.has(targetId);
  const dim = size === "sm" ? "w-8 h-8" : "w-10 h-10";
  const icon = size === "sm" ? "w-4 h-4" : "w-5 h-5";

  const onClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!targetId) return;
    if (!user) {
      toast(LABELS[resolvedKind].signIn, {
        action: { label: "Sign in", onClick: () => navigate("/auth") },
      });
      return;
    }
    const nowFav = await toggle(targetId);
    if (nowFav) toast.success(LABELS[resolvedKind].added);
  };

  return (
    <button
      onClick={onClick}
      aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
      aria-pressed={isFav}
      className={`${dim} grid place-items-center rounded-full bg-background/90 backdrop-blur shadow-card hover:scale-110 transition-transform ${className}`}
    >
      <Heart
        className={`${icon} transition-colors ${isFav ? "fill-primary text-primary" : "text-foreground"}`}
      />
    </button>
  );
};

export default FavoriteButton;
